import * as React from 'react';
import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import List from 'antd/lib/list';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import Text from 'antd/lib/typography/Text';
import * as API from '../../api';
import { useDispatch, useSelector } from 'react-redux';
import { AppComponentProps } from '../../typescriptHelpers';
import { AppSettingsUpdatable } from '../../actions/updatable';
import { RootState } from '../../reducers/reducers';
import { selectAuthCredentials } from '../../selectors/selectors';
import { tryUpdateAppSettings } from '../../actions/appSettings';
import { globalMessage } from '../../utils/globalMessage';
import { BumpVersionFile } from '../../types';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface BumpVersionFilesModalProps extends AppComponentProps {
    open: boolean;
    initialFiles: BumpVersionFile[];
    onCancel: () => void;
}

const BumpVersionFilesModal: React.FC<BumpVersionFilesModalProps> = (props: BumpVersionFilesModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const [filenames, setFilenames] = React.useState(
        props.initialFiles ? props.initialFiles.map((file) => file.fileName) : [],
    );

    const editBumpFilesIsLoading = useIsLoading(AppSettingsUpdatable.UpdateBumpVersionOverrideFiles);
    const editBumpFilesError = useError(AppSettingsUpdatable.UpdateBumpVersionOverrideFiles);
    const editBumpFilesSuccess = useUpdated(AppSettingsUpdatable.UpdateBumpVersionOverrideFiles);

    const [isGetVersionFilesLoading, setIsGetVersionFilesLoading] = React.useState(false);
    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));

    const getDefaultBumpVersionFiles = async () => {
        setIsGetVersionFilesLoading(true);
        try {
            const fetchedDefaultFiles = await API.getDefaultBumpVersionFiles(
                { orgId: props.orgId, appId: props.appId },
                authCredentials,
            );
            setFilenames(fetchedDefaultFiles.map((file) => file.fileName));
        } catch (error) {
            globalMessage.error(`Error getting default files: ${error.message}`);
        }
        setIsGetVersionFilesLoading(false);
    };

    const saveBumpVersionFiles = (newFilenames) => {
        dispatch(
            tryUpdateAppSettings(AppSettingsUpdatable.UpdateBumpVersionOverrideFiles, {
                orgId: props.orgId,
                appId: props.appId,
                files: newFilenames,
            }),
        );
    };

    React.useEffect(() => {
        if (editBumpFilesError) {
            globalMessage.error(editBumpFilesError);
        }
    }, [editBumpFilesError]);

    React.useEffect(() => {
        if (editBumpFilesSuccess) {
            globalMessage.success('Bump version files updated!');

            props.onCancel();
        }
    }, [editBumpFilesSuccess]);

    return (
        <Modal
            title="Edit bump version files"
            footer={
                <div style={{ display: 'flex' }}>
                    <Button
                        type="link"
                        loading={isGetVersionFilesLoading}
                        onClick={getDefaultBumpVersionFiles}
                        disabled={editBumpFilesIsLoading}
                    >
                        Find existing version files
                    </Button>
                    <div style={{ flex: 1 }} />
                    <Button onClick={props.onCancel}>Cancel</Button>
                    <Button
                        type="primary"
                        loading={editBumpFilesIsLoading}
                        onClick={() => saveBumpVersionFiles(filenames)}
                        disabled={isGetVersionFilesLoading}
                    >
                        Save
                    </Button>
                </div>
            }
            open={props.open}
            onCancel={props.onCancel}
            afterClose={() => {
                setFilenames(props.initialFiles.map((file) => file.fileName));
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text>
                    Specify the file path(s) for your app&apos;s version files. Runway will bump the version in these
                    files during manual or automated version bumps.
                </Text>

                <Form
                    form={form}
                    style={{
                        display: 'flex',
                        marginTop: 24,
                    }}
                    onFinish={(values) => {
                        const newFilename = values.filename;
                        if (newFilename) {
                            setFilenames((prev) => [...prev, newFilename]);
                            form.resetFields();
                        }
                    }}
                >
                    <Form.Item
                        style={{ marginRight: 12, flex: 1 }}
                        name="filename"
                        rules={[{ required: true, message: 'Please enter a file path.' }]}
                    >
                        <Input placeholder={`path/some_file.extension`} autoFocus allowClear />
                    </Form.Item>

                    <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
                        Add
                    </Button>
                </Form>

                <List
                    style={{ backgroundColor: 'white' }}
                    bordered
                    size="small"
                    locale={{ emptyText: 'No files added yet' }}
                    dataSource={filenames}
                    renderItem={(filename, index) => (
                        <List.Item key={index}>
                            <div style={{ display: 'flex', flex: 1, minWidth: 0 }}>
                                <div
                                    style={{
                                        flex: 1,
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {filename}
                                </div>
                                <Button
                                    danger
                                    size="small"
                                    key={index}
                                    icon={<DeleteOutlined />}
                                    type="link"
                                    onClick={() =>
                                        setFilenames((prev) => {
                                            const newFilenames = [...prev];
                                            newFilenames.splice(index, 1);
                                            return newFilenames;
                                        })
                                    }
                                />
                            </div>
                        </List.Item>
                    )}
                />
            </div>
        </Modal>
    );
};

export default BumpVersionFilesModal;
